/**
 * Fuente única del catálogo de ayuda (FUNC-12; D-27, D-53).
 *
 * Por qué es un módulo de datos plano `.mjs` y sin importaciones: lo leen dos consumidores que no
 * comparten runtime. El envoltorio tipado (`articulos.ts`) lo usan las páginas de Next.js, y el
 * generador (`scripts/generarCopiaAyuda.mjs`) corre bajo `node` puro, que no puede importar `.ts`.
 * Mantener los datos aquí garantiza que la página pública y la copia que cita el chat salgan del
 * mismo texto.
 *
 * Reglas de redacción (D-56, «nunca inventa»): cada afirmación se ha comprobado contra el código de
 * esta rama y del backend/worker hermanos; lo que el código no sostiene no se escribe. La
 * `respuestaCorta` es la frase exacta que el chat cita al terapeuta, así que no lleva abreviaturas
 * con punto (el validador cuenta frases) ni promete nada condicional sin su condición.
 *
 * Campos: slug, titulo, respuestaCorta (1 a 3 frases), cuerpo (párrafos), palabrasClave,
 * enlaceEnApp (destino de tablero permitido por el backend, o null), actualizadoEn (YYYY-MM-DD) y,
 * solo en los artículos que vienen de una pregunta frecuente, faq { id, categoria }.
 *
 * `abreSubida: true` (opcional, G-12-8, E8-29): el «Abrir en el tablero →» del chat abre Documentos con
 * el diálogo de subida ya abierto. Es un dato explícito y no se deduce de «el destino es Documentos»,
 * para que un artículo que enlaza a Documentos por otro motivo no abra un diálogo. Solo es válido con
 * `enlaceEnApp.seccion === 'documents'`; el generador lo valida y lo copia al backend.
 */

const ARTICULOS = [
    {
        slug: 'subir-documentos-antiguos',
        titulo: 'Subir documentos antiguos o notas en papel',
        respuestaCorta:
            'Hazles una foto o escanéalas y arrástralas a Documentos. El asistente las transcribe y usa la fecha escrita en ellas.',
        cuerpo: [
            'En la sección Documentos de la ficha del paciente, arrastra el archivo a la zona de subida o pulsa «Subir documento». Se admiten PDF, Word (.doc y .docx) e imágenes JPG y PNG, de hasta 10 MB cada una, y se suben de una en una.',
            'Las fotos y los escaneos de notas manuscritas se leen con reconocimiento de texto. El resultado puede tener errores: abre la transcripción desde Documentos, compárala con la imagen original y corrígela si hace falta.',
            'Para la fecha usamos la que aparezca escrita en el documento; si no la lleva, la del archivo, y si tampoco, la de subida. Siempre puedes cambiarla, y cómo se decide está explicado en «Cómo se decide la fecha real de un documento».',
        ],
        palabrasClave: [
            'subir',
            'subo',
            'subida',
            'documentos antiguos',
            'notas antiguas',
            'notas en papel',
            'papel',
            'escanear',
            'escaneo',
            'foto',
            'fotografía',
            'manuscritas',
            'manuscrito',
            'escritas a mano',
            'a mano',
        ],
        enlaceEnApp: { seccion: 'documents', recursoId: null },
        abreSubida: true,
        actualizadoEn: '2026-10-01',
    },
    {
        slug: 'fecha-real-de-un-documento',
        titulo: 'Cómo se decide la fecha real de un documento',
        respuestaCorta:
            'La fecha real es la que hayas puesto tú a mano. Si no la has cambiado, se usa la fecha escrita en el propio documento; si no hay ninguna, la de los metadatos del archivo y, en último lugar, la de subida. En los tests funciona igual, pero sin fecha escrita se usa la de su sesión.',
        cuerpo: [
            'Cada documento tiene una fecha real, que es la que aparece en la cronología y en la lista de documentos. Se decide en este orden: la que hayas puesto tú a mano; si no, la fecha escrita en el propio documento; si no hay ninguna, la de los metadatos del archivo (por ejemplo, la de creación de un PDF o la de una foto) y, en último lugar, la de subida.',
            'La fecha escrita y la de los metadatos solo se aceptan si no son posteriores al día de la subida ni anteriores a 1990. Además, se descartan las fechas por defecto que algunos programas escriben en los metadatos cuando no conocen la real.',
            'Para cambiarla, usa el lápiz que hay junto a la fecha en la cronología o «Cambiar fecha» en el menú del documento. Si ya la habías corregido, «Volver a la fecha automática» devuelve la que decide el sistema.',
        ],
        palabrasClave: [
            'fecha real',
            'fecha',
            'fechas',
            'cambiar fecha',
            'corregir fecha',
            'corrijo',
            'origen de la fecha',
            'fecha escrita',
            'metadatos',
            'cronología',
            'ordenar por fecha',
        ],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
    },

    // --- Una guía por función disponible (D-27). Cada una describe solo lo que el código de esta rama hace.
    // El artículo del plan describe la sección Plan tal como existe aquí y hay que volver a comprobarlo
    // cuando la Fase 10 se fusione.
    {
        slug: 'psicometria',
        titulo: 'Psicometría: las pruebas de tu paciente en un solo perfil',
        respuestaCorta:
            'Psicometría reúne en un perfil las puntuaciones de las pruebas de tu paciente a lo largo de su historial, con su evolución entre evaluaciones. El asistente solo describe lo que aparece en los documentos y la interpretación la escribes tú.',
        cuerpo: [
            'El perfil se construye con las puntuaciones que figuran en los documentos de las pruebas que has subido a la ficha del paciente. TherapyRecap no calcula puntuaciones a partir de las respuestas sueltas de un cuestionario: lee las que ya vienen en el documento.',
            'Verás una tabla con todas las evaluaciones y un gráfico que compara la última con la anterior. Si el documento no trae percentil, la escala aparece sin marcador y solo con su puntuación directa.',
            'Si una escala empeora, o dos escalas relacionadas se mueven en sentidos opuestos, aparece una «Discrepancia a explorar» con las sesiones revisadas entre las dos evaluaciones. Es una pista para tu criterio, no una conclusión.',
            'Puedes corregir lo que se haya leído mal con «Revisar puntuaciones»: lo que escribas queda marcado como «Corregida por ti». Mientras haya pruebas sin confirmar, el perfil te lo avisa.',
            'Bajo el gráfico, el asistente redacta un borrador que describe las puntuaciones, y puedes editarlo o regenerarlo. «Tu lectura clínica» es un espacio para que escribas tú tu interpretación: el asistente no la redacta. «Exportar PDF» guarda el perfil con el gráfico, la tabla, la descripción y tu lectura.',
        ],
        palabrasClave: [
            'psicometria',
            'psicométrica',
            'pruebas psicométricas',
            'tests psicométricos',
            'perfil psicométrico',
            'puntuaciones',
            'escalas',
            'percentil',
            'evolución de las pruebas',
            'funciona la psicometría',
            'exportar perfil',
            'discrepancia a explorar',
        ],
        enlaceEnApp: { seccion: 'assistant/psychometry', recursoId: null },
        actualizadoEn: '2026-10-01',
    },
    {
        slug: 'manuscritos',
        titulo: 'Manuscritos: transcribir notas y cuestionarios escritos a mano',
        respuestaCorta:
            'Las fotos y los escaneos de documentos escritos a mano se transcriben, y puedes revisar el texto junto a la imagen original. Si un cuestionario escaneado está incompleto, el sistema te avisa de que faltan páginas y no lo usa en Psicometría hasta que esté completo.',
        cuerpo: [
            'Sube la foto o el escaneo en Documentos (JPG, PNG o PDF). Mientras se lee aparece «Leyendo el documento…» y, cuando termina, lo abres con «Revisar transcripción».',
            '«Revisar transcripción» abre un panel con el original a un lado, con zoom, y la transcripción al otro. El texto leído de una letra manuscrita puede tener errores: léelo entero frente a la imagen y corrígelo con «Editar texto» y «Guardar» si hace falta.',
            'Si escaneas un cuestionario de varias páginas y falta alguna, o hay una repetida, verás una banda con las miniaturas de las páginas y la frase «No se usa en Psicometría hasta que esté completo.» Desde ahí puedes subir la página que falta con «Subir la página N» o quitar el duplicado.',
            'Las fechas escritas en el documento se usan como fecha real, como se explica en «Cómo se decide la fecha real de un documento».',
        ],
        palabrasClave: [
            'manuscritos',
            'manuscrito',
            'escrito a mano',
            'transcripción de manuscritos',
            'revisar transcripción',
            'cuestionario escaneado',
            'páginas del cuestionario',
            'página que falta',
            'quitar duplicado',
        ],
        enlaceEnApp: { seccion: 'assistant/handwritten', recursoId: null },
        actualizadoEn: '2026-10-01',
    },
    {
        slug: 'carpetas',
        titulo: 'Organizar los documentos en carpetas',
        respuestaCorta:
            'Puedes organizar los documentos de un paciente en carpetas que creas, renombras y eliminas tú. Al eliminar una carpeta sus documentos no se borran: pasan a «Sin carpeta».',
        cuerpo: [
            'En la sección Documentos, el árbol de la izquierda agrupa la lista por «Carpetas», «Fecha» o «Tipo». Con «+ Nueva carpeta» creas una, y desde el menú «⋯» de cada carpeta la renombras o la eliminas.',
            'Para mover un documento, arrástralo a una carpeta del árbol o usa «Mover a carpeta…» en el menú de su fila. «Quitar de la carpeta» lo devuelve a «Sin carpeta».',
            'El asistente también puede proponerte una organización en carpetas desde el chat: la propuesta solo se aplica cuando la apruebas.',
        ],
        palabrasClave: [
            'carpetas',
            'carpeta',
            'organizar documentos',
            'mover documentos',
            'nueva carpeta',
            'renombrar carpeta',
            'eliminar carpeta',
            'sin carpeta',
        ],
        enlaceEnApp: { seccion: 'assistant/folders', recursoId: null },
        actualizadoEn: '2026-10-01',
    },
    {
        slug: 'cronologia',
        titulo: 'Cronología del caso',
        respuestaCorta:
            'La cronología coloca las sesiones y los documentos del paciente en una línea temporal según su fecha real. Puedes filtrar entre sesiones y documentos y cambiar la fecha de cualquier elemento.',
        cuerpo: [
            'Se abre desde el tablero del paciente como «Cronología del caso». Junto a la línea temporal hay una tabla con la fecha, el tipo, el nombre y el origen de la fecha de cada elemento, y el selector «Mostrar» alterna entre «Todo», «Sesiones» y «Documentos».',
            'Los documentos que no llevan ninguna fecha escrita se marcan como «Sin fecha en el documento · fecha de subida». Con el lápiz de cada fila cambias la fecha, y la cronología se reordena.',
            'En pantallas estrechas la cronología se muestra como una lista en lugar de una línea temporal.',
        ],
        palabrasClave: [
            'cronología',
            'cronologia',
            'línea temporal',
            'linea del tiempo',
            'orden de sesiones',
            'fechas de los documentos',
            'cronología del caso',
        ],
        enlaceEnApp: { seccion: 'assistant/chronology', recursoId: null },
        actualizadoEn: '2026-10-01',
    },
    {
        slug: 'notas-fijas',
        titulo: 'Notas fijas: lo que quieres que el asistente tenga presente',
        respuestaCorta:
            'Las notas fijas son frases tuyas que el asistente tiene presentes en las conversaciones sobre ese paciente. Puedes guardar hasta 20 por paciente, de hasta 500 caracteres cada una, y editarlas o borrarlas cuando quieras.',
        cuerpo: [
            'Aparecen en una banda bajo la cabecera del chat, con las tres más recientes a la vista y «Ver las N» para el resto.',
            'Puedes añadirlas tú o pedírselo al asistente en el chat. Cuando lo pides, te pregunta «¿Lo guardo como nota fija?» y solo la guarda si respondes que sí, con tus palabras tal cual, sin reescribirlas.',
            'Son solo tuyas y solo se usan en las conversaciones del paciente al que pertenecen.',
        ],
        palabrasClave: [
            'notas fijas',
            'nota fija',
            'recordar al asistente',
            'instrucciones para el asistente',
            'guardar una nota',
            'banda de notas',
        ],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
    },
    {
        slug: 'adjuntos-en-el-chat',
        titulo: 'Adjuntar archivos en el chat',
        respuestaCorta:
            'Puedes arrastrar un archivo al chat del paciente, o adjuntarlo con el botón del chat, hasta cinco por mensaje. El asistente lo lee y te propone dónde guardarlo en Documentos, y nada se organiza hasta que apruebas la propuesta.',
        cuerpo: [
            'Se aceptan los mismos tipos que en Documentos: PDF, Word (.doc y .docx), JPG y PNG. Mientras se lee aparece una tarjeta «Leyendo…» con el nombre del archivo.',
            'Cuando termina, el asistente te propone el tipo de documento, el título, la carpeta, la sesión y la fecha. Puedes aprobar la propuesta tal cual, editarla o rechazarla.',
            'Si el archivo no se puede leer, el aviso te ofrece «Volver a subir».',
        ],
        palabrasClave: [
            'adjuntar',
            'adjunto',
            'adjuntos',
            'archivo en el chat',
            'arrastrar al chat',
            'propuesta de destino',
            'leyendo',
        ],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
    },
    {
        slug: 'plan-de-tratamiento',
        titulo: 'Plan de tratamiento',
        respuestaCorta:
            'El plan de tratamiento se organiza en objetivos, cada uno con su indicador, y en tareas entre sesiones; lo construyes tú en la sección Plan de la ficha o a partir de propuestas del asistente. Las propuestas llegan para que las apruebes, las edites o las rechaces, y nada se guarda hasta que las apruebas. Cada cambio aprobado queda en el «Historial de cambios».',
        cuerpo: [
            'En la sección Plan defines el enfoque y la fecha de próxima revisión, los objetivos con su indicador (medida, valor de partida, valor actual y meta) y las tareas entre sesiones, con su frecuencia. Puedes añadir, editar y retirar objetivos y tareas a mano.',
            'Para pedirle una propuesta al asistente necesitas tener registrado tu diagnóstico. El asistente puede proponer objetivos, tareas y avances a partir de tus sesiones, de lo que le pidas o de lo que le dictes en el chat. Cada propuesta se enseña con su cita de origen y no se guarda hasta que la apruebas; puedes editarla antes o rechazarla.',
            'El «Historial de cambios» se abre en un panel junto al plan y recoge cada cambio con quién lo aprobó y cuándo. Lo que retiras sale de la lista, pero queda en el historial.',
            'Si un paciente tenía un plan escrito como texto libre, se conserva en solo lectura como «Plan anterior». Con «Convertir» el asistente lo transforma en una propuesta de objetivos y tareas que también apruebas tú.',
        ],
        palabrasClave: [
            'plan de tratamiento',
            'plan',
            'objetivos terapéuticos',
            'trasladar al plan',
            'propuesta de plan',
            'objetivos',
            'tareas',
            'historial de cambios',
            'plan anterior',
            'convertir plan',
        ],
        enlaceEnApp: { seccion: 'plan', recursoId: null },
        actualizadoEn: '2026-10-01',
    },

    // --- Las preguntas frecuentes del centro de ayuda (18), comprobadas una a una contra el código (D-56).
    // Se migran diecisiete: «security-2» se retira porque ningún código puede sostener que nadie del
    // equipo acceda a los datos. La respuesta corta es la respuesta que muestra el acordeón de /ayuda.
    {
        slug: 'como-empezar-a-usar-therapy-recap',
        titulo: '¿Cómo empiezo a usar Therapy Recap?',
        respuestaCorta:
            'Crea una cuenta con tu correo electrónico o con tu cuenta de Google y añade a tu paciente. En su ficha, pulsa «Nueva sesión» para subir un audio o «Grabar» para grabar en el momento.',
        cuerpo: [
            'Cuando el audio termina de procesarse, la sesión aparece en la ficha con su transcripción y su resumen.',
            'Antes de subir un audio, el paciente debe tener registrado su consentimiento para el tratamiento de datos de salud.',
        ],
        palabrasClave: ['empezar', 'empiezo', 'primeros pasos', 'crear cuenta', 'registrarme', 'primera sesión', 'nueva sesión', 'grabar'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'setup-1', categoria: 'SETUP' },
    },
    {
        slug: 'instalar-algo-en-el-ordenador',
        titulo: '¿Tengo que instalar algo en mi ordenador?',
        respuestaCorta: 'No. Therapy Recap funciona desde el navegador, sin instalar ningún programa ni aplicación.',
        cuerpo: ['Basta con iniciar sesión desde el navegador de tu ordenador, tu tablet o tu móvil.'],
        palabrasClave: ['instalar', 'instalación', 'programa', 'aplicación', 'descargar', 'navegador'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'setup-2', categoria: 'SETUP' },
    },
    {
        slug: 'usar-desde-el-movil-o-la-tablet',
        titulo: '¿Puedo usarlo desde el móvil o la tablet?',
        respuestaCorta:
            'Sí. La aplicación se adapta a móviles y tablets desde el navegador, y puedes grabar la sesión con el micrófono del dispositivo.',
        cuerpo: ['La primera vez, el navegador te pedirá permiso para usar el micrófono: tienes que aceptarlo para poder grabar.'],
        palabrasClave: ['móvil', 'movil', 'tablet', 'ipad', 'teléfono', 'responsive', 'grabar desde el móvil'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'setup-3', categoria: 'SETUP' },
    },
    {
        slug: 'seguridad-y-privacidad-de-los-datos',
        titulo: '¿Therapy Recap es seguro y cumple con las normativas de privacidad?',
        respuestaCorta:
            'Los nombres y otros datos identificativos de las transcripciones se sustituyen por marcas antes de que la transcripción se guarde en la ficha, y la correspondencia se guarda cifrada. El audio original se borra del almacenamiento cuando termina de guardarse la transcripción.',
        cuerpo: ['Para conocer las medidas de seguridad y de cumplimiento normativo con detalle, consulta la página de Seguridad o escribe a info@therapyrecap.com.'],
        palabrasClave: ['seguro', 'seguridad', 'privacidad', 'rgpd', 'normativa', 'anonimización', 'protección de datos', 'audio eliminado'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-07',
        faq: { id: 'security-1', categoria: 'SECURITY' },
    },
    {
        slug: 'donde-se-almacenan-los-datos',
        titulo: '¿Dónde se almacenan los datos?',
        respuestaCorta:
            'Los archivos y el procesamiento están en Google Cloud Platform, y los datos de las cuentas y de los pacientes se guardan en bases de datos gestionadas (PostgreSQL y MongoDB Atlas).',
        cuerpo: ['La página de Seguridad resume las medidas de protección aplicadas a esos datos.'],
        palabrasClave: ['almacenan', 'almacenamiento', 'dónde están los datos', 'servidores', 'google cloud', 'base de datos'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'security-3', categoria: 'SECURITY' },
    },
    {
        slug: 'precio-de-fundador',
        titulo: '¿Qué incluye el precio de fundador?',
        respuestaCorta:
            'Es el plan Professional con el precio de fundador: sesiones ilimitadas con una política de uso razonable, memoria clínica, asistente de IA, buscador DSM-5 de referencia y espacio de trabajo personalizable. El importe vigente está en la página de Precios.',
        cuerpo: ['Puedes ver y cambiar tu plan desde «Suscripción», en el menú de tu cuenta.'],
        palabrasClave: ['oferta fundadores', 'precio de fundador', 'plan professional', 'precio', 'cuánto cuesta', 'descuento'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'pricing-1', categoria: 'PRICING' },
    },
    {
        slug: 'cancelar-la-suscripcion',
        titulo: '¿Puedo cancelar mi suscripción en cualquier momento?',
        respuestaCorta:
            'Sí, desde «Suscripción», en el menú de tu cuenta. Al cancelar sigues con acceso hasta que termina el periodo de facturación en curso, y puedes reactivarla antes de esa fecha.',
        cuerpo: ['Desde esa misma pantalla puedes abrir el portal de facturación de Stripe para gestionar tu facturación.'],
        palabrasClave: ['cancelar', 'cancelo', 'cancelación', 'suscripción', 'darme de baja', 'baja', 'reactivar', 'facturación'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'pricing-2', categoria: 'PRICING' },
    },
    {
        slug: 'metodos-de-pago',
        titulo: '¿Qué métodos de pago aceptáis?',
        respuestaCorta: 'El pago se hace con tarjeta de crédito o débito a través de Stripe.',
        cuerpo: ['El cobro lo gestiona Stripe. Desde «Suscripción» puedes abrir su portal de facturación.'],
        palabrasClave: ['métodos de pago', 'pago', 'tarjeta', 'stripe', 'pagar', 'cobro'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'pricing-3', categoria: 'PRICING' },
    },
    {
        slug: 'clinicas-y-muchos-pacientes',
        titulo: '¿Es Therapy Recap adecuado para clínicas o si atiendo a muchos pacientes al mes?',
        // 17-REVIEW-C WR-03: Starter tiene tope de sesiones (LIMITE_SESIONES_STARTER del backend) y ese tope mira el
        // plan propio del usuario, no la clínica, así que no se promete «sesiones ilimitadas» ni «licencias
        // Professional». Las cifras las fija clinica-sin-sesiones-ilimitadas.test.ts contra planesPrecios.ts.
        respuestaCorta:
            'Starter incluye 40 sesiones al mes y Professional no tiene tope mensual de sesiones. Para equipos existen los planes de clínica, con facturación unificada y panel de administración. Cada profesional de la clínica tiene todas las funciones y 319 preguntas al asistente al mes.',
        cuerpo: ['Cada terapeuta de la organización ve solo sus propios pacientes: la organización comparte licencias y facturación, no el historial clínico.'],
        palabrasClave: ['clínica', 'clínicas', 'equipo', 'organización', 'muchos pacientes', 'licencias', 'plan clinic', 'varios terapeutas'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-07',
        faq: { id: 'pricing-4', categoria: 'PRICING' },
    },
    {
        slug: 'documentos-que-genera-la-ia',
        titulo: '¿Qué tipo de documentos clínicos genera la IA?',
        respuestaCorta:
            'Genera la transcripción de cada sesión, un resumen y notas de progreso en cinco formatos: SOAP, DAP, BIRP, GIRP y PIRP. Todo lo que genera es un borrador que revisas tú.',
        cuerpo: [
            'Además, desde la ficha del paciente puedes pedir un análisis descriptivo de tu diagnóstico registrado: cuenta en cuántas sesiones se menciona cada criterio, con una cita literal, y no propone ni puntúa diagnósticos.',
            'El plan de tratamiento lo escribes tú; el asistente solo puede proponerte cambios, que se guardan si los aceptas.',
        ],
        palabrasClave: ['documentos clínicos', 'qué genera', 'notas de progreso', 'soap', 'dap', 'birp', 'resumen', 'transcripción'],
        enlaceEnApp: { seccion: 'notes', recursoId: null },
        actualizadoEn: '2026-10-01',
        faq: { id: 'clinical-1', categoria: 'CLINICAL' },
    },
    {
        slug: 'editar-lo-que-genera-la-ia',
        titulo: '¿Puedo editar lo que genera la IA?',
        respuestaCorta:
            'Depende del documento: puedes editar las notas de progreso, las secciones de los documentos clínicos, los objetivos y tareas del plan y la transcripción de un manuscrito, pero no el resumen de una sesión. Todo lo que genera la IA es un borrador que revisas tú.',
        cuerpo: ['También puedes corregir la transcripción de un documento escrito a mano desde «Revisar transcripción».'],
        palabrasClave: ['editar', 'modificar', 'corregir', 'resúmenes', 'borrador', 'cambiar el texto', 'editar notas'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'clinical-2', categoria: 'CLINICAL' },
    },
    {
        slug: 'sesiones-de-pareja-o-familia',
        titulo: '¿Qué pasa si atiendo parejas o familias?',
        respuestaCorta:
            'La transcripción distingue dos interlocutores, terapeuta y paciente, y etiqueta cada intervención con uno de los dos. En una sesión con más de dos personas no identifica a cada una por separado.',
        cuerpo: ['Si trabajas con parejas o familias, revisa la transcripción antes de apoyarte en ella para atribuir quién dijo qué.'],
        palabrasClave: ['parejas', 'familias', 'pareja', 'familia', 'varios hablantes', 'hablantes', 'quién habla', 'terapia de pareja'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'clinical-3', categoria: 'CLINICAL' },
    },
    {
        slug: 'exportar-a-pdf',
        titulo: '¿Cómo exporto mis documentos?',
        respuestaCorta:
            'Desde la ficha del paciente, «Informe PDF» genera el informe del paciente. Los documentos por secciones se exportan con «Exportar PDF» o «Exportar DOCX», las plantillas clínicas solo en PDF, y el perfil psicométrico con «Exportar PDF» desde Psicometría.',
        cuerpo: ['Los informes y documentos se generan en tu navegador y se descargan como archivo.'],
        palabrasClave: ['exportar', 'exporto', 'pdf', 'word', 'docx', 'documento por secciones', 'descargar', 'informe', 'imprimir', 'informe pdf'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'clinical-4', categoria: 'CLINICAL' },
    },
    {
        slug: 'subir-documentos-a-la-ficha',
        titulo: '¿Puedo subir documentos a la ficha de un paciente?',
        respuestaCorta:
            'Sí, desde la sección Documentos de la ficha: PDF, Word (.doc y .docx) e imágenes JPG y PNG, de hasta 10 MB cada uno. El asistente de chat puede consultar esos documentos al responder sobre el paciente.',
        cuerpo: ['Los documentos se suben de uno en uno. Durante el periodo de prueba hay un límite de 10 documentos por cuenta.'],
        palabrasClave: ['subir documentos', 'documentos del paciente', 'ficha', 'pdf', 'word', 'imágenes', 'archivos', 'adjuntar a la ficha'],
        enlaceEnApp: { seccion: 'documents', recursoId: null },
        abreSubida: true,
        actualizadoEn: '2026-10-01',
        faq: { id: 'clinical-5', categoria: 'CLINICAL' },
    },
    {
        slug: 'el-microfono-no-funciona',
        titulo: 'Mi micrófono no parece funcionar, ¿qué hago?',
        respuestaCorta:
            'Comprueba que has dado permiso de micrófono a tu navegador: suele aparecer un icono en la barra de direcciones desde el que puedes permitirlo. Revisa también la configuración de sonido de tu sistema operativo.',
        cuerpo: ['Si has denegado el permiso, vuelve a activarlo desde los ajustes del sitio en tu navegador y recarga la página.'],
        palabrasClave: ['micrófono', 'microfono', 'no graba', 'no se oye', 'no funciona', 'permisos', 'sonido', 'grabación'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'technical-1', categoria: 'TECHNICAL' },
    },
    {
        slug: 'formatos-de-audio',
        titulo: '¿Qué formatos de audio puedo subir si ya tengo la grabación?',
        respuestaCorta: 'Puedes subir audio en MP3, WAV, M4A, MP4 y WEBM, de uno en uno.',
        cuerpo: [
            'En la ficha del paciente, pulsa «Nueva sesión» y elige el archivo.',
            'Antes de subirlo, el paciente debe tener registrado su consentimiento para el tratamiento de datos de salud.',
        ],
        palabrasClave: ['formatos', 'audio', 'mp3', 'wav', 'm4a', 'mp4', 'webm', 'subir grabación', 'archivo de audio'],
        enlaceEnApp: null,
        actualizadoEn: '2026-10-01',
        faq: { id: 'technical-2', categoria: 'TECHNICAL' },
    },
    {
        slug: 'la-transcripcion-no-termina',
        titulo: '¿Qué hago si la transcripción se queda cargando y no termina?',
        respuestaCorta:
            'El tiempo depende de la duración del audio. Si la sesión sigue en «Transcribiendo…» mucho más tiempo del habitual, recarga la página y, si persiste, escribe a info@therapyrecap.com indicando la sesión.',
        cuerpo: ['Una sesión que está esperando turno se muestra como «En cola» hasta que empieza a procesarse.'],
        palabrasClave: ['transcripción', 'transcribiendo', 'cargando', 'no termina', 'procesando', 'tarda', 'atascada', 'en cola'],
        enlaceEnApp: { seccion: 'sessions', recursoId: null },
        actualizadoEn: '2026-10-01',
        faq: { id: 'technical-3', categoria: 'TECHNICAL' },
    },
];

export const ARTICULOS_AYUDA = Object.freeze(ARTICULOS.map((articulo) => Object.freeze(articulo)));
